import { useState, useEffect } from 'react';

interface BannerData {
  active: boolean;
  message: string;
  type: string;
  color: string;
  text_color: string;
  dismissible: boolean;
  id: string;
  expires_at?: string | null;
  audiences?: string[];
}

const PORTAL_ID = 'workstation';

export default function SystemBanner() {
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchBanners = () => {
      fetch('/api/system-settings/banners')
        .then(res => (res.ok ? res.json() : []))
        .then((arr: BannerData[]) => {
          const now = Date.now();
          const eligible = (Array.isArray(arr) ? arr : []).filter(b => {
            if (!b || !b.active) return false;
            if (b.expires_at && new Date(b.expires_at).getTime() < now) return false;
            const audiences = Array.isArray(b.audiences) ? b.audiences : [];
            if (!audiences.includes(PORTAL_ID)) return false;
            if (localStorage.getItem(`dismissed_banner_${b.id}`)) return false;
            return true;
          });
          setBanners(eligible);
        })
        .catch(() => setBanners([]));
    };

    fetchBanners();
    const interval = setInterval(fetchBanners, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = (id: string) => {
    localStorage.setItem(`dismissed_banner_${id}`, 'true');
    setDismissed(prev => ({ ...prev, [id]: true }));
  };

  const visible = banners.filter(b => !dismissed[b.id]);
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map(banner => (
        <div
          key={banner.id}
          className="px-4 py-3 flex items-center justify-between"
          style={{ backgroundColor: banner.color, color: banner.text_color }}
        >
          <p className="text-sm font-medium flex-1 text-center">{banner.message}</p>
          {banner.dismissible && (
            <button
              onClick={() => handleDismiss(banner.id)}
              className="ml-4 opacity-70 hover:opacity-100 transition-opacity flex-shrink-0"
              style={{ color: banner.text_color }}
              aria-label="Dismiss banner"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ))}
    </>
  );
}
